import {
    HttpEvent,
    HttpInterceptorFn,
    HttpResponse,
} from '@angular/common/http';
import { map } from 'rxjs';
import { looksLikeIso, toBackendOffsetString, coerceDate } from './date-format';
import { DATE_SCHEMAS, DateSchema, getSchemaKey } from './date-schemas';
import { inject } from '@angular/core';
import { DATE_UI_MODE, DateUIMode } from './date-ui.providers';

function floorToLocalMidnight(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
}

// ---- Type guards to protect uploads / binary ----
const isFormData = (v: any): v is FormData =>
    typeof FormData !== 'undefined' && v instanceof FormData;

const isBlobOrFile = (v: any): v is Blob | File =>
    (typeof Blob !== 'undefined' && v instanceof Blob) ||
    (typeof File !== 'undefined' && v instanceof File);

// ---- Regex helpers (unchanged) ----
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/; // 2025-08-27
const ISO_DATETIME_NO_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/; // 2025-08-27T10:30[:45]
const HAS_TZ_OFFSET_OR_Z = /[Zz]|[+\-]\d{2}:\d{2}$/; // ...Z or ...+03:00
const SLASHED = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/; // ##/##/####

// Interpret "##/##/####" according to global mode
function parseSlashedDateByMode(s: string, mode: DateUIMode): Date | null {
    const m = s.trim().match(SLASHED);
    if (!m) return null;
    const a = +m[1],
        b = +m[2],
        yyyy = +m[3];
    const mm = mode === 'US' ? a : b;
    const dd = mode === 'US' ? b : a;
    const d = new Date(yyyy, mm - 1, dd, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
}

// ---- Transform out (request) ----
function toBackend(
    val: any,
    key: string,
    schema?: DateSchema,
    mode: DateUIMode = 'US'
): any {
    if (!schema) return val;
    const isDateOnly = schema.dateOnly?.includes(key);
    const isDateTime = schema.dateTime?.includes(key);
    if (!isDateOnly && !isDateTime) return val;

    if (val == null) return null;

    if (val instanceof Date) {
        return toBackendOffsetString(
            isDateOnly ? floorToLocalMidnight(val) : val
        );
    }

    if (typeof val === 'string') {
        const s = val.trim();

        // 👇 NEW: interpret slashed date by mode (US: MM/DD/YYYY, EU: DD/MM/YYYY)
        const byMode = parseSlashedDateByMode(s, mode);
        if (byMode) {
            return toBackendOffsetString(
                isDateOnly ? floorToLocalMidnight(byMode) : byMode
            );
        }

        if (isDateOnly) {
            if (ISO_DATE_ONLY.test(s)) {
                const d = new Date(s);
                return toBackendOffsetString(floorToLocalMidnight(d));
            }
            if (HAS_TZ_OFFSET_OR_Z.test(s)) return s;
            if (looksLikeIso(s)) {
                const d = coerceDate(s);
                return d ? toBackendOffsetString(floorToLocalMidnight(d)) : s;
            }
        }

        if (isDateTime) {
            if (HAS_TZ_OFFSET_OR_Z.test(s)) return s;
            if (ISO_DATE_ONLY.test(s)) {
                const d = new Date(s);
                return toBackendOffsetString(d);
            }
            if (ISO_DATETIME_NO_TZ.test(s)) {
                const d = new Date(s);
                return toBackendOffsetString(d);
            }
            if (looksLikeIso(s)) {
                const d = coerceDate(s);
                return d ? toBackendOffsetString(d) : s;
            }
        }
    }

    const d = coerceDate(val);
    return d
        ? toBackendOffsetString(isDateOnly ? floorToLocalMidnight(d) : d)
        : val;
}

// ---- Transform in (response) ----
function fromBackend(val: any, key: string, schema?: DateSchema): any {
    if (!schema) return val;
    const isDateOnly = schema.dateOnly?.includes(key);
    const isDateTime = schema.dateTime?.includes(key);
    if (!isDateOnly && !isDateTime) return val;

    if (typeof val === 'string' && looksLikeIso(val)) {
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
            return isDateOnly ? floorToLocalMidnight(d) : d;
        }
    }
    return val;
}

// ---- Safe deep transform (does NOT touch FormData / File / Blob / Date) ----
function deepTransform(
    value: any,
    transform: (key: string, v: any) => any
): any {
    if (Array.isArray(value)) {
        return value.map((v) => deepTransform(v, transform));
    }
    if (value instanceof Date || isFormData(value) || isBlobOrFile(value)) {
        return value;
    }
    if (value && typeof value === 'object') {
        const out: any = {};
        for (const [k, v] of Object.entries(value)) {
            const rec = deepTransform(v, transform);
            out[k] = transform(k, rec);
        }
        return out;
    }
    return value;
}

/**
 * Prevent internal form-only fields from leaking into EPCIS event payloads.
 * The top-level harvest_locations field used by Landing forms is left intact.
 */
function sanitizeEventPayload(value: any): any {
    if (Array.isArray(value)) {
        return value.map((item) => sanitizeEventPayload(item));
    }
    if (value instanceof Date || isFormData(value) || isBlobOrFile(value)) {
        return value;
    }
    if (!value || typeof value !== 'object') {
        return value;
    }

    const out: any = {};
    for (const [key, nestedValue] of Object.entries(value)) {
        const sanitizedValue = sanitizeEventPayload(nestedValue);

        if ((key === 'agri:meta' || key === 'agriMeta') && sanitizedValue && typeof sanitizedValue === 'object') {
            const sanitizedMeta = { ...sanitizedValue };
            delete sanitizedMeta.harvest_locations;
            delete sanitizedMeta.harvestLocations;
            out[key] = sanitizedMeta;
        } else if (key === 'bizTransactionList' && Array.isArray(sanitizedValue)) {
            out[key] = sanitizedValue.map((transaction) => {
                if (!transaction || typeof transaction !== 'object' || Array.isArray(transaction)) {
                    return transaction;
                }
                const sanitizedTransaction = { ...transaction };
                delete sanitizedTransaction.issuer;
                return sanitizedTransaction;
            });
        } else {
            out[key] = sanitizedValue;
        }
    }

    return out;
}

// ---- Interceptor ----
export const dateTransportInterceptor: HttpInterceptorFn = (req, next) => {
    // ⛔ Do NOT transform uploads; leave FormData/binary as-is
    if (req.body && isFormData(req.body)) {
        return next(req);
    }

    const mode = inject(DATE_UI_MODE); // 👈 global mode
    const key = getSchemaKey(req.url, req.body);
    const schema = key ? DATE_SCHEMAS[key] : undefined;

    const transformedBody =
        req.body != null
            ? deepTransform(req.body, (k, v) => toBackend(v, k, schema, mode))
            : req.body;
    const bodyOut = sanitizeEventPayload(transformedBody);

    const cloned = bodyOut !== req.body ? req.clone({ body: bodyOut }) : req;

    return next(cloned).pipe(
        map<HttpEvent<any>, HttpEvent<any>>((event) => {
            if (event instanceof HttpResponse && event.body != null) {
                const bodyIn = deepTransform(event.body, (k, v) =>
                    fromBackend(v, k, schema)
                );
                return event.clone({ body: bodyIn });
            }
            return event;
        })
    );
};
